import React, { useState } from 'react';
import { Sparkles, Check, ArrowRight, FlaskConical, FileText } from 'lucide-react';
import { Card } from '../../shared/components/Card';
import { Button } from '../../shared/components/Button';
import { NavRoute } from '../../components/layout/Sidebar';
import { useToast } from '../../shared/context/ToastContext';
import { COVER_TEMPLATES } from '../../templates/cover';
import { LAB_REPORT_TEMPLATES } from '../../templates/lab-report';
import { useDocument } from '../../core/state/DocumentContext';

interface TemplatesViewProps {
  onNavigate: (route: NavRoute) => void;
}

interface TemplateEntry {
  id: string;
  name: string;
  category: 'cover' | 'report' | 'project';
  targetStudio: NavRoute;
  description: string;
  tags: string[];
  isDefault?: boolean;
  onApply?: () => void;
}

export const TemplatesView: React.FC<TemplatesViewProps> = ({ onNavigate }) => {
  const { showToast } = useToast();
  const { loadDocument, document: currentDoc } = useDocument();
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'cover' | 'report' | 'project'>('all');

  // Convert COVER_TEMPLATES into gallery entries
  const coverEntries: TemplateEntry[] = COVER_TEMPLATES.map((tpl) => ({
    id: tpl.id,
    name: tpl.name,
    category: 'cover',
    targetStudio: 'cover-studio',
    description: tpl.description,
    tags: ['Cover Page', tpl.category.replace('_', ' '), 'A4'],
    isDefault: tpl.id === 'tpl-ewu-standard',
  }));

  // Convert LAB_REPORT_TEMPLATES into gallery entries
  const reportEntries: TemplateEntry[] = LAB_REPORT_TEMPLATES.map((tpl) => ({
    id: tpl.id,
    name: tpl.name,
    category: 'report',
    targetStudio: 'report-studio',
    description: tpl.description,
    tags: ['Lab Report', tpl.category.toUpperCase(), `${tpl.sectionsBlueprint.length} Sections`],
    isDefault: tpl.id === 'tpl-lab-standard',
    onApply: () => {
      const doc = tpl.compileToDocument(tpl.name, currentDoc.pageSettings);
      loadDocument(doc);
    },
  }));

  const allTemplates: TemplateEntry[] = [...coverEntries, ...reportEntries];

  const filtered = selectedCategory === 'all'
    ? allTemplates
    : allTemplates.filter((t) => t.category === selectedCategory);

  const handleUseTemplate = (tpl: TemplateEntry) => {
    if (tpl.onApply) {
      tpl.onApply();
    }
    showToast(`Loaded template: "${tpl.name}".`, 'success');
    onNavigate(tpl.targetStudio);
  };

  return (
    <div style={{ padding: 'var(--space-8)', display: 'flex', flexDirection: 'column', gap: 'var(--space-6)', maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 'var(--space-4)' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <h1 style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700 }}>Template Library</h1>
            <span className="badge badge-primary">
              <Sparkles size={11} /> {allTemplates.length} Templates Available
            </span>
          </div>
          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
            Start from standardized academic templates designed for university coursework, laboratory experiments, and engineering project reports.
          </p>
        </div>
      </div>

      {/* Category Filter Pills */}
      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        {[
          { id: 'all', label: `All Templates (${allTemplates.length})` },
          { id: 'cover', label: `Cover Pages (${coverEntries.length})` },
          { id: 'report', label: `Laboratory Reports (${reportEntries.length})` },
        ].map((cat) => (
          <button
            key={cat.id}
            type="button"
            onClick={() => setSelectedCategory(cat.id as any)}
            className={`btn ${selectedCategory === cat.id ? 'btn-primary' : 'btn-secondary'} btn-sm`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Templates Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 'var(--space-5)' }}>
        {filtered.map((tpl) => (
          <Card
            key={tpl.id}
            style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 'var(--space-4)' }}
          >
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-2)' }}>
                <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--bg-surface-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary-600)' }}>
                  {tpl.category === 'cover' ? <FileText size={18} /> : <FlaskConical size={18} />}
                </div>
                {tpl.isDefault && (
                  <span className="badge badge-success" style={{ fontSize: '10px' }}>
                    <Check size={11} /> Recommended
                  </span>
                )}
              </div>

              <h3 style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 'var(--space-1)' }}>
                {tpl.name}
              </h3>
              <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 'var(--space-3)' }}>
                {tpl.description}
              </p>

              <div style={{ display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap' }}>
                {tpl.tags.map((tag, i) => (
                  <span key={i} className="badge badge-neutral" style={{ fontSize: '10px' }}>
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <Button
              variant="secondary"
              size="sm"
              icon={<ArrowRight size={14} />}
              iconPosition="right"
              onClick={() => handleUseTemplate(tpl)}
              style={{ width: '100%' }}
            >
              Use This Template
            </Button>
          </Card>
        ))}
      </div>
    </div>
  );
};
